import { useId, useState, type ReactNode } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "js/components/ui/collapsible";
import {
  Card,
  CardContent,
  CardHeader,
} from "js/components/ui/card";
import { Button } from "js/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "js/components/ui/tooltip";
import { cn } from "lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Pencil, type LucideIcon } from "lucide-react";

interface CollapsibleSectionProps {
  title: string;
  icon: LucideIcon;
  defaultOpen?: boolean;
  badge?: ReactNode;
  onEdit?: () => void;
  editDisabled?: boolean;
  editTooltip?: string;
  children: ReactNode;
}

function CollapsibleSection({
  title,
  icon: Icon,
  defaultOpen = true,
  badge,
  onEdit,
  editDisabled,
  editTooltip,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  const headingId = `${id}-heading`;
  const contentId = `${id}-content`;

  return (
    <section aria-labelledby={headingId}>
      <Collapsible open={open} onOpenChange={setOpen} asChild>
        <Card className="overflow-hidden py-0">
        <CollapsibleTrigger asChild>
          <CardHeader
            className={cn(
              "cursor-pointer select-none py-4 transition-colors hover:bg-muted/50",
              !open && "pb-4",
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Icon className="size-4.5 text-muted-foreground" />
                <h3
                  id={headingId}
                  className="text-base font-semibold leading-none"
                >
                  {title}
                </h3>
                {badge}
              </div>

              <div className="flex items-center gap-1.5">
                {onEdit && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {editTooltip && editDisabled ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0}>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={editDisabled}
                              onClick={onEdit}
                              aria-label={`Editar ${title}`}
                            >
                              <Pencil className="size-3.5" />
                              Editar
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{editTooltip}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={editDisabled}
                        onClick={onEdit}
                        aria-label={`Editar ${title}`}
                      >
                        <Pencil className="size-3.5" />
                        Editar
                      </Button>
                    )}
                  </div>
                )}
                <ChevronDown
                  className={cn(
                    "size-4 text-muted-foreground transition-transform duration-200",
                    open && "rotate-180",
                  )}
                />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <AnimatePresence initial={false}>
          {open && (
            <CollapsibleContent forceMount asChild>
              <motion.div
                id={contentId}
                role="region"
                aria-labelledby={headingId}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                style={{ overflow: "hidden" }}
              >
                <CardContent className="pt-0 pb-5">
                  {children}
                </CardContent>
              </motion.div>
            </CollapsibleContent>
          )}
        </AnimatePresence>
        </Card>
      </Collapsible>
    </section>
  );
}

export default CollapsibleSection;
